import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, Row, Col, Alert, Spinner, ButtonGroup, Button } from 'react-bootstrap';
import Select from 'react-select';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { workoutAPI } from '../services/workoutAPI';

function ExerciseProgressChart({ exercises }) {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [progressData, setProgressData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', 'area'
  const [metricType, setMetricType] = useState('auto'); // 'auto', 'weight', 'reps', 'volume', 'distance', 'pace', 'duration'
  const [exercisesWithLogs, setExercisesWithLogs] = useState(new Set());
  const [isCheckingLogs, setIsCheckingLogs] = useState(true);

  // Filter out archived exercises for selection
  const activeExercises = useMemo(() => exercises.filter(e => !e.is_archived), [exercises]);

  // Check which exercises have logs
  useEffect(() => {
    const checkExerciseLogs = async () => {
      setIsCheckingLogs(true);
      const exercisesWithData = new Set();

      if (activeExercises.length === 0) {
        setIsCheckingLogs(false);
        return;
      }

      // Check all active exercises in parallel
      const checks = activeExercises.map(async (ex) => {
        try {
          const result = await workoutAPI.checkExerciseHasLogs(ex.id);
          if (result.hasLogs) {
            exercisesWithData.add(ex.id);
          }
        } catch (err) {
          console.error(`Error checking logs for exercise ${ex.id}:`, err);
        }
      });

      await Promise.all(checks);
      setExercisesWithLogs(exercisesWithData);
      setIsCheckingLogs(false);
    };

    checkExerciseLogs();
  }, [activeExercises]);

  // Format exercises for react-select - only show exercises with logs
  const exerciseOptions = useMemo(() => {
    return activeExercises
      .filter(ex => exercisesWithLogs.has(ex.id))
      .map(ex => ({
        value: ex.id,
        label: `${ex.name} (${ex.exercise_type.replace('_', ' ')})`,
        exerciseData: ex
      }));
  }, [activeExercises, exercisesWithLogs]);

  useEffect(() => {
    if (selectedExercise) {
      fetchProgressData(selectedExercise.id);
    }
  }, [selectedExercise]);

  const fetchProgressData = async (exerciseId) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await workoutAPI.fetchExerciseProgressData(exerciseId);
      setProgressData(data);

      // Auto-select appropriate metric based on exercise type
      if (metricType === 'auto') {
        determineDefaultMetric(selectedExercise.exercise_type);
      }
    } catch (err) {
      console.error('Error fetching progress data:', err);
      setError('Failed to load progress data.');
    } finally {
      setIsLoading(false);
    }
  };

  const determineDefaultMetric = (exerciseType) => {
    // Auto-select best metric for exercise type
    switch (exerciseType) {
      case 'weight_reps':
      case 'weight_time':
        setMetricType('weight');
        break;
      case 'cardio_basic':
      case 'cardio_segments':
        setMetricType('distance');
        break;
      default:
        setMetricType('weight');
    }
  };

  const formatChartData = () => {
    if (!progressData || progressData.length === 0) return [];

    return progressData.map(item => {
      const date = new Date(item.workout_date);
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Calculate pace (min/mile or min/km) for cardio
      let pace = null;
      if (item.max_distance && item.best_time) {
        const distanceValue = parseFloat(item.max_distance);
        const timeInMinutes = parseFloat(item.best_time) / 60;
        pace = distanceValue > 0 ? timeInMinutes / distanceValue : null;
      }

      return {
        date: formattedDate,
        fullDate: item.workout_date,
        weight: item.max_weight ? parseFloat(item.max_weight) : null,
        reps: item.max_reps ? parseInt(item.max_reps) : null,
        volume: item.total_volume ? parseFloat(item.total_volume) : null,
        distance: item.max_distance ? parseFloat(item.max_distance) : null,
        duration: item.best_time ? parseFloat(item.best_time / 60) : null, // Convert to minutes
        pace: pace
      };
    }).filter(item => {
      // Filter out data points where the selected metric is null
      const metric = getMetricConfig().dataKey;
      return item[metric] !== null && item[metric] !== undefined;
    });
  };

  const getMetricConfig = () => {
    const configs = {
      weight: {
        dataKey: 'weight',
        name: 'Max Weight (lbs)',
        color: '#8884d8',
        yAxisLabel: 'Weight (lbs)'
      },
      reps: {
        dataKey: 'reps',
        name: 'Max Reps',
        color: '#82ca9d',
        yAxisLabel: 'Reps'
      },
      volume: {
        dataKey: 'volume',
        name: 'Total Volume (lbs)',
        color: '#ffc658',
        yAxisLabel: 'Volume (lbs)'
      },
      distance: {
        dataKey: 'distance',
        name: 'Distance (miles)',
        color: '#ff7c7c',
        yAxisLabel: 'Distance (miles)'
      },
      duration: {
        dataKey: 'duration',
        name: 'Duration (min)',
        color: '#a78bfa',
        yAxisLabel: 'Duration (min)'
      },
      pace: {
        dataKey: 'pace',
        name: 'Pace (min/mile)',
        color: '#fb923c',
        yAxisLabel: 'Pace (min/mile)'
      }
    };
    return configs[metricType] || configs.weight;
  };

  const renderChart = () => {
    const chartData = formatChartData();
    const config = getMetricConfig();

    if (chartData.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
          <p className="text-muted">No workout data available for this exercise and metric.</p>
        </div>
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 }
    };

    const ChartComponent = chartType === 'bar' ? BarChart : chartType === 'area' ? AreaChart : LineChart;
    const DataComponent = chartType === 'bar' ? Bar : chartType === 'area' ? Area : Line;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis
            label={{ value: config.yAxisLabel, angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            labelStyle={{ color: '#000' }}
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
          />
          <Legend />
          <DataComponent
            type="monotone"
            dataKey={config.dataKey}
            stroke={config.color}
            fill={config.color}
            name={config.name}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  const getAvailableMetrics = () => {
    if (!selectedExercise) return [];

    const type = selectedExercise.exercise_type;

    if (type === 'weight_reps' || type === 'weight_time') {
      return [
        { value: 'weight', label: 'Max Weight' },
        { value: 'reps', label: 'Max Reps' },
        { value: 'volume', label: 'Total Volume' }
      ];
    } else if (type === 'cardio_basic' || type === 'cardio_segments') {
      return [
        { value: 'distance', label: 'Distance' },
        { value: 'duration', label: 'Duration' },
        { value: 'pace', label: 'Pace' }
      ];
    }
    return [];
  };

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-3">
          <i className="fas fa-chart-area me-2"></i>
          Exercise Progress
        </h5>
        <Row className="align-items-end g-2">
          <Col md={4}>
            <Form.Label className="small mb-1">Select Exercise</Form.Label>
            <Select
              options={exerciseOptions}
              value={exerciseOptions.find(opt => opt.value === selectedExercise?.id) || null}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setSelectedExercise(selectedOption.exerciseData);
                  setMetricType('auto');
                } else {
                  setSelectedExercise(null);
                }
              }}
              isClearable
              isLoading={isCheckingLogs}
              isDisabled={isCheckingLogs}
              placeholder={isCheckingLogs ? "Loading exercises..." : "Search or select an exercise..."}
              noOptionsMessage={() => "No exercises with workout data found"}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  fontSize: '0.875rem'
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: '0.875rem'
                })
              }}
            />
          </Col>

          {selectedExercise && (
            <>
              <Col md={3}>
                <Form.Label className="small mb-1">Metric</Form.Label>
                <Form.Select
                  value={metricType}
                  onChange={(e) => setMetricType(e.target.value)}
                >
                  {getAvailableMetrics().map(metric => (
                    <option key={metric.value} value={metric.value}>
                      {metric.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label className="small mb-1">Chart Type</Form.Label>
                <ButtonGroup size="sm" className="w-100">
                  <Button
                    variant={chartType === 'line' ? 'primary' : 'outline-primary'}
                    onClick={() => setChartType('line')}
                  >
                    <i className="fas fa-chart-line"></i>
                  </Button>
                  <Button
                    variant={chartType === 'bar' ? 'primary' : 'outline-primary'}
                    onClick={() => setChartType('bar')}
                  >
                    <i className="fas fa-chart-bar"></i>
                  </Button>
                  <Button
                    variant={chartType === 'area' ? 'primary' : 'outline-primary'}
                    onClick={() => setChartType('area')}
                  >
                    <i className="fas fa-chart-area"></i>
                  </Button>
                </ButtonGroup>
              </Col>
            </>
          )}
        </Row>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading progress data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : selectedExercise ? (
          renderChart()
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-search fa-3x text-muted mb-3"></i>
            <p className="text-muted">Select an exercise to view progress charts</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ExerciseProgressChart;
